import { useMemo } from "react";
import type { Gender } from "@gooner/shared";
import {
  GENDERS,
  GENDER_LABELS,
  SECURITY_QUESTIONS,
  ageOn,
} from "@gooner/shared";
import { Field, Select, TextInput, cx } from "../ui/primitives";

/**
 * The account fields that sign-up and /settings both render.
 *
 * They exist here rather than twice because the two surfaces ask the SAME questions and
 * must refuse them the same way — and because the email note below is a promise, and a
 * promise written in two places is a promise that will eventually be made in two
 * different wordings.
 */

/**
 * The whole reason an email is on the form, stated where it is collected.
 *
 * Exported so the sign-up card and the settings page say it identically. The privacy
 * policy makes the same promise in its own prose; that one is necessarily separate, so
 * the two are kept in step by being changed in the same commit.
 */
export const EMAIL_NEVER_NOTE =
  "We will never email you. It's here only so you can get back into your account if you forget your password.";

/** The latest birthday that clears `minAge` — the date input's own ceiling. */
export function maxBirthday(minAge: number, now = Date.now()): string {
  const d = new Date(now);
  d.setUTCFullYear(d.getUTCFullYear() - minAge);
  return d.toISOString().slice(0, 10);
}

/**
 * Date of birth — ONE native date input, not three selects.
 *
 * That argument is already settled in this repo: AgeGate.tsx deleted exactly the
 * three-select form and lists what it cost ("a hundred years of options, the rejection
 * of 31 February, the day-before-your-birthday edge case"). Rebuilding it three files
 * away would be the codebase arguing with itself. The native control gets a real picker
 * on a phone, rejects impossible dates for free, and is one tab stop.
 *
 * `[color-scheme:dark]` is load-bearing, not decoration: without it Chrome paints the
 * calendar glyph and the whole picker light-on-light against --color-ink.
 *
 * `max` is advisory — typing beats it in some browsers — so dobError still runs in JS,
 * and the server still runs it again. This only makes the obvious answer the easy one.
 */
export function DobField({
  value,
  onChange,
  minAge,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  minAge: number;
  disabled?: boolean;
}) {
  const max = useMemo(() => maxBirthday(minAge), [minAge]);
  return (
    <Field label="Date of birth" hint={`${minAge}+`}>
      <TextInput
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min="1900-01-01"
        max={max}
        autoComplete="bday"
        disabled={disabled}
        className="[color-scheme:dark]"
      />
    </Field>
  );
}

/** The five, from shared. The disabled placeholder keeps "never answered" distinct from
 *  "Prefer not to say", which is a real answer somebody chose. */
export function GenderField({
  value,
  onChange,
  disabled,
}: {
  value: Gender | "";
  onChange: (v: Gender | "") => void;
  disabled?: boolean;
}) {
  return (
    <Field label="Gender">
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as Gender | "")}
        disabled={disabled}
      >
        <option value="" disabled>
          Choose…
        </option>
        {GENDERS.map((g) => (
          <option key={g} value={g}>
            {GENDER_LABELS[g]}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/**
 * One of the three security questions, with its answer.
 *
 * `taken` is the other two slots' question ids. An already-picked question is rendered
 * DISABLED rather than filtered out: removing an option shortens the list under a
 * finger mid-scroll and hides why the question vanished. Greyed-and-present answers the
 * question the user is actually asking, which is "where did that go".
 *
 * The answer is `type="text"`, never `type="password"`. Nobody is shoulder-surfing
 * their own sign-up, and an answer you cannot re-read is how a typo becomes a locked
 * account six months later.
 */
export function SecurityQuestionRow({
  index,
  questionId,
  answer,
  taken,
  onQuestion,
  onAnswer,
  disabled,
}: {
  index: number;
  questionId: string;
  answer: string;
  taken: ReadonlyArray<string>;
  onQuestion: (id: string) => void;
  onAnswer: (text: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Select
        aria-label={`Security question ${index + 1}`}
        value={questionId}
        onChange={(e) => onQuestion(e.target.value)}
        disabled={disabled}
      >
        <option value="" disabled>
          Pick a question…
        </option>
        {SECURITY_QUESTIONS.map((q) => (
          <option key={q.id} value={q.id} disabled={q.id !== questionId && taken.includes(q.id)}>
            {q.prompt}
          </option>
        ))}
      </Select>
      <TextInput
        aria-label={`Answer ${index + 1}`}
        value={answer}
        onChange={(e) => onAnswer(e.target.value)}
        placeholder="Your answer"
        autoComplete="off"
        spellCheck={false}
        autoCapitalize="none"
        disabled={disabled}
      />
    </div>
  );
}

/** A fixed-height line under a field, so typing never makes the card jump. */
export function FieldNote({ tone, children }: { tone?: "ok" | "bad"; children: React.ReactNode }) {
  return (
    <p
      className={cx(
        "mt-1 min-h-[0.9rem] text-[11px] leading-snug",
        tone === "ok" ? "text-emerald-400" : tone === "bad" ? "text-red-400" : "text-zinc-500",
      )}
    >
      {children}
    </p>
  );
}

/** Whole years old today, or null — re-exported so callers need one import. */
export const ageToday = (dob: string) => ageOn(dob, Date.now());
